import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["aria-label"]
const _hoisted_2 = { class: "save-btn-container" }

import ResumeBase from "@/Layouts/ResumeBase/ResumeBase.vue";
import TextInput from "@/components/Common/Inputs/TextInput/TextInput.vue";
import CheckboxInput from "@/components/Common/Inputs/CheckboxInput/CheckboxInput.vue";
import AppButton from "@/components/Common/AppButton/AppButton.vue";
import { useResumeStore, useErrorStore } from "@/store";
import { ContactDetails } from "@/store/models/resume.models";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, onBeforeRouteLeave, useRoute } from "vue-router";
import { onIonViewDidEnter } from "@ionic/vue";
import { handleBeforeUnload, focusOnMainContent } from "@/helpers";
import { IonInputCustomEvent } from "@ionic/core/components";
import { collateErrorsInDescription } from "@/helpers";


export default /*@__PURE__*/_defineComponent({
  __name: 'ResumeContactDetails',
  setup(__props) {

const { t } = useI18n<{}>({
  useScope: "global",
});
const resumeStore = useResumeStore();
const errorStore = useErrorStore();
const router = useRouter();
const route = useRoute();
const skipOnRouteBeforeLeave = ref(false);

onBeforeRouteLeave(() => {
  resumeStore.setContactDetailsFormInit(false);
  window.removeEventListener("beforeunload", handleBeforeUnload);
});

onMounted(async () => {
  if (!resumeStore.resumeId) {
    await resumeStore.requestResume();
    if (!resumeStore.resumeId) {
      await resumeStore.createResume();
    }
  }
  resumeStore.initContactDetailsForm();
});

onIonViewDidEnter(() => {
  focusOnMainContent(route?.meta?.mainContentId);
  skipOnRouteBeforeLeave.value = false;
  window.addEventListener("beforeunload", handleBeforeUnload);
});

const firstName = computed(() => {
  return resumeStore.resume?.participant?.givenName;
});

const lastName = computed(() => {
  return resumeStore.resume?.participant?.familyName;
});

const email = computed(() => {
  return resumeStore.resume?.participant?.email;
});

const phone = computed(() => {
  return resumeStore.resume?.participant?.mobilePhone;
});

const location = computed(() => {
  return resumeStore.resume?.participant?.location;
});

const hideLocation = computed(() => {
  return resumeStore.contactDetailsFormData?.hideLocation;
});

const linkedinId = computed(() => {
  return resumeStore.contactDetailsFormData?.linkedinId;
});

const isFormSaving = ref(false);

const onInputChange = <K extends keyof ContactDetails>(
  property: K,
  value: ContactDetails[K] | null | undefined
) => {
  if (value == null || !property) return;
  resumeStore.setContactDetailsFormValue(property, value);
};

const saveForm = async () => {
  skipOnRouteBeforeLeave.value = true;
  errorStore.setSaveFormError(false);
  const contactDetailsId = resumeStore.resumeContactDetails?.id;
  try {
    isFormSaving.value = true;
    if (contactDetailsId) {
      await resumeStore.updateContactDetails(contactDetailsId);
    } else {
      await resumeStore.createContactDetails();
    }
    await resumeStore.updateResumeProperty("isContactDetailsCompleted", true);
    router.push({
      name: "ResumeOverview",
    });
  } catch (error) {
    console.error("An error occured saving the form: ", error);
    errorStore.setSaveFormError(true);
  } finally {
    isFormSaving.value = false;
  }
};

const errorIdPrefix = ref("error-message-disabler");
const saveButtonDescription = ref("");
const updateErrorDescription = () => {
  saveButtonDescription.value = collateErrorsInDescription(
    document,
    errorIdPrefix.value
  );
};

const returnToPage = () => {
  router.push({
    name: "ResumeOverview",
  });
};

/* VALIDATION */

const rules = {
  linkedinId: {
    invalid: t("resume.contactDetailsForm.linkedinInputError"),
  },
};

const validationMap = ref<Map<keyof ContactDetails, boolean>>(
  new Map([["linkedinId", true]])
);

const errorMessageMap = ref<Map<keyof ContactDetails, string>>(
  new Map([["linkedinId", ""]])
);

const isFormSavingEnabled = computed(() => {
  for (let isValid of validationMap.value.values()) {
    if (!isValid) return false;
  }
  return true;
});

const setValidity = (
  property: keyof ContactDetails,
  valid: boolean,
  error?: string
): void => {
  validationMap.value.set(property, valid);
  errorMessageMap.value.set(property, error ?? "");
  return;
};

const linkedinIdValidation = (event: IonInputCustomEvent<FocusEvent>) => {
  const itemEl = event.target.parentElement;
  itemEl?.classList.remove("ion-invalid");
  const regex = /^(https?:\/\/)?(www\.)?linkedin\.com\/(?!slink(\?|\/|$)).*$/;
  const value = event.target.value as string;
  if (value && !regex.test(value)) {
    itemEl?.classList.add("ion-invalid");
    setValidity("linkedinId", false, rules.linkedinId.invalid);
  } else {
    setValidity("linkedinId", true);
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ResumeBase, {
    breadcrumbs: [
      {
        route: 'contact-details',
        label: _unref(t)('resume.breadcrumbs.contactDetails'),
      },
    ],
    mainContentId: "resume-contact-details-main-content",
    formSaveRetryFunction: saveForm,
    skipOnRouteBeforeLeave: skipOnRouteBeforeLeave.value
  }, {
    title: _withCtx(() => [
      _createTextVNode(_toDisplayString(_unref(t)("resume.contactDetailsForm.title")), 1)
    ]),
    description: _withCtx(() => [
      _createTextVNode(_toDisplayString(_unref(t)("resume.contactDetailsForm.description")), 1)
    ]),
    "additional-note": _withCtx(() => [
      _createTextVNode(_toDisplayString(_unref(t)("resume.contactDetailsForm.additionalNote")), 1)
    ]),
    "main-content": _withCtx(() => [
      _createElementVNode("form", {
        "aria-label": _unref(t)('resume.contactDetailsForm.title')
      }, [
        _createVNode(TextInput, {
          "data-test": "name-input",
          labelText: _unref(t)('resume.contactDetailsForm.nameInputLabel'),
          value: `${firstName.value} ${lastName.value}`,
          isLoading: _unref(resumeStore).isResumeLoading,
          readonly: true
        }, null, 8, ["labelText", "value", "isLoading"]),
        _createVNode(TextInput, {
          "data-test": "email-input",
          labelText: _unref(t)('resume.contactDetailsForm.emailInputLabel'),
          value: `${email.value}`,
          isLoading: _unref(resumeStore).isResumeLoading,
          readonly: true
        }, null, 8, ["labelText", "value", "isLoading"]),
        _createVNode(TextInput, {
          "data-test": "phone-input",
          labelText: _unref(t)('resume.contactDetailsForm.phoneInputLabel'),
          value: `${phone.value}`,
          isLoading: _unref(resumeStore).isResumeLoading,
          readonly: true
        }, null, 8, ["labelText", "value", "isLoading"]),
        _createVNode(TextInput, {
          "data-test": "location-input",
          class: "location-input",
          labelText: _unref(t)('resume.contactDetailsForm.locationInputLabel'),
          value: location.value || '',
          placeholderText: 
            _unref(t)('resume.contactDetailsForm.locationInputPlaceholder')
          ,
          isLoading: _unref(resumeStore).isResumeLoading,
          readonly: true
        }, null, 8, ["labelText", "value", "placeholderText", "isLoading"]),
        _createVNode(CheckboxInput, {
          "data-test": "hide-location-input",
          checked: hideLocation.value || false,
          disabled: !location.value,
          property: "hideLocation",
          labelText: _unref(t)('resume.contactDetailsForm.hideLocationInputLabel'),
          isLoading: _unref(resumeStore).isResumeLoading,
          onOnChange: _cache[0] || (_cache[0] = (value) => onInputChange('hideLocation', value))
        }, null, 8, ["checked", "disabled", "labelText", "isLoading"]),
        _createVNode(TextInput, {
          "data-test": "linkedin-input",
          property: "linkedinId",
          labelText: _unref(t)('resume.contactDetailsForm.linkedinInputLabel'),
          inputType: "url",
          placeholderText: 
            _unref(t)('resume.contactDetailsForm.linkedinInputPlaceholder')
          ,
          value: linkedinId.value || '',
          errorText: errorMessageMap.value.get('linkedinId'),
          isLoading: _unref(resumeStore).isResumeLoading,
          errorIdPrefix: errorIdPrefix.value,
          onOnUpdate: updateErrorDescription,
          onOnChange: _cache[1] || (_cache[1] = (event) => onInputChange('linkedinId', event.detail.value)),
          onOnBlur: linkedinIdValidation
        }, null, 8, ["labelText", "placeholderText", "value", "errorText", "isLoading", "errorIdPrefix"]),
        _createElementVNode("div", _hoisted_2, [
          _createVNode(AppButton, {
            size: "default",
            type: "primary",
            label: _unref(t)('resume.contactDetailsForm.saveButtonText'),
            ariaLabel: _unref(t)('resume.contactDetailsForm.saveButtonText'),
            onClick: saveForm,
            "data-test": "save-form-btn",
            class: "save-form-btn",
            width: "100%",
            isLoading: isFormSaving.value,
            disabled: !isFormSavingEnabled.value,
            ariaRoleDescription: saveButtonDescription.value
          }, null, 8, ["label", "ariaLabel", "isLoading", "disabled", "ariaRoleDescription"]),
          _createVNode(AppButton, {
            size: "default",
            type: "textOnly",
            label: _unref(t)('resume.contactDetailsForm.closeButtonText'),
            ariaLabel: _unref(t)('resume.contactDetailsForm.closeButtonText'),
            "data-test": "close-form-btn",
            class: "close-form-btn",
            id: "closeBtn",
            onClick: returnToPage,
            width: "100%"
          }, null, 8, ["label", "ariaLabel"])
        ])
      ], 8, _hoisted_1)
    ]),
    "resume-helper": _withCtx(() => [...(_cache[2] || (_cache[2] = []))]),
    _: 1
  }, 8, ["breadcrumbs", "skipOnRouteBeforeLeave"]))
}
}

})